import { useCabinetSubscription, useCurrentCabinetId } from "@/hooks/useCabinetSubscription";
import { useLicenseExempt } from "@/hooks/useLicenseExempt";

// Paywall kill switch — when not 'true', skip the pricing-page redirect for
// every user. Mirrors the backend getLicenseUsage() switch and the add-in
// flag (TICKET-069). Today, billing is handled out-of-band via a manually
// sent Stripe link, so the pricing page is dormant by design. Re-enable by
// setting VITE_PAYWALL_ENFORCEMENT_ENABLED=true on the Vercel project.
const PAYWALL_ENFORCEMENT_ENABLED =
  import.meta.env.VITE_PAYWALL_ENFORCEMENT_ENABLED === 'true';

interface PricingPageState {
  isLoading: boolean;
  shouldShowPricingPage: boolean;
  shouldRedirectAway: boolean;
  canAccessApp: boolean;
  
  // Data needed by PricingPage component
  licenseUsage: any;
  isAdmin: boolean;
  checkoutLoading: boolean;
  wasInvited: boolean;
  isNewCabinet: boolean;
  
  // Actions — price id + URLs de retour résolus côté backend (/api/stripe/create-checkout-session)
  redirectToCabinetCheckout: (quantity: number) => Promise<void>;
  markCabinetAsVisited: () => Promise<void>;
}

const determinePricingNeed = (data: {
  isLicenseExempt: boolean;
  wasInvited: boolean;
  hasValidSubscription: boolean;
  subscription: any;
  isFirstTimeInCabinet: boolean;
}): boolean => {
  const { isLicenseExempt, wasInvited, hasValidSubscription, subscription, isFirstTimeInCabinet } = data;
  
  // Staff users never need pricing
  if (isLicenseExempt) {
    console.log('PricingPageState: Staff user - no pricing needed');
    return false;
  }
  
  // Invited users never need pricing
  if (wasInvited) {
    console.log('PricingPageState: Invited user - no pricing needed');
    return false;
  }
  
  // Check invitation flow context
  const currentUrl = window.location.pathname + window.location.search;
  const hasInvitationContext = currentUrl.includes('cabinetId=') || 
                              sessionStorage.getItem('justCompletedProfile') === 'true';
  const redirectAfterLogin = sessionStorage.getItem('redirectAfterLogin');
  const isFromInviteFlow = redirectAfterLogin && redirectAfterLogin.includes('/invite/');
  
  if (hasInvitationContext || isFromInviteFlow) {
    console.log('PricingPageState: User in invitation flow - no pricing needed');
    return false;
  }
  
  // Show pricing for trialing users on first visit to cabinet (regardless of hasValidSubscription)
  const shouldShowPricing = subscription?.status === 'trialing' && isFirstTimeInCabinet;
  
  // Users with valid subscription AND who are not first-time visitors don't need pricing
  if (hasValidSubscription && !shouldShowPricing) {
    console.log('PricingPageState: User has valid subscription and not first time - no pricing needed');
    return false;
  }
  
  console.log('PricingPageState: Final decision:', {
    shouldShowPricing,
    subscriptionStatus: subscription?.status,
    isFirstTimeInCabinet,
    hasValidSubscription
  });
  
  return shouldShowPricing;
};

export const usePricingPageState = (): PricingPageState => {
  // ALL HOOKS MUST BE CALLED UNCONDITIONALLY AT THE TOP
  const { isLicenseExempt, isLoading: isLicenseExemptLoading } = useLicenseExempt();
  const currentCabinetId = useCurrentCabinetId();
  const cabinetSub = useCabinetSubscription(currentCabinetId ?? undefined);

  // Kill switch: skip every pricing/redirect decision when paywall enforcement
  // is disabled (current default). canAccessApp=true so consumers don't gate UI.
  if (!PAYWALL_ENFORCEMENT_ENABLED) {
    return {
      isLoading: false,
      shouldShowPricingPage: false,
      shouldRedirectAway: false,
      canAccessApp: true,
      licenseUsage: cabinetSub.licenseUsage,
      isAdmin: cabinetSub.isAdmin,
      checkoutLoading: cabinetSub.checkoutLoading,
      wasInvited: cabinetSub.wasInvited,
      isNewCabinet: cabinetSub.isNewCabinet,
      redirectToCabinetCheckout: cabinetSub.redirectToCabinetCheckout,
      markCabinetAsVisited: cabinetSub.markCabinetAsVisited,
    };
  }
  
  // SINGLE LOADING STATE - wait for ALL pricing-relevant data
  const isLoading = isLicenseExemptLoading || 
                   cabinetSub.isLoading || 
                   !currentCabinetId;
  
  // While loading: Allow full app access (don't block anything)
  if (isLoading) {
    console.log('PricingPageState: Loading state - allowing full app access');
    return {
      isLoading: true,
      shouldShowPricingPage: false,    // Don't show pricing yet
      shouldRedirectAway: false,       // Don't redirect yet
      canAccessApp: true,              // Full app access during loading
      
      // Provide safe defaults during loading
      licenseUsage: null,
      isAdmin: false,
      checkoutLoading: false,
      wasInvited: false,
      isNewCabinet: false,
      
      // Safe no-op functions during loading
      redirectToCabinetCheckout: () => Promise.resolve(),
      markCabinetAsVisited: async () => {}
    };
  }
  
  // NOW we have confirmed data - make pricing decision
  const shouldShowPricing = determinePricingNeed({
    isLicenseExempt,
    wasInvited: cabinetSub.wasInvited,
    hasValidSubscription: cabinetSub.hasValidSubscription,
    subscription: cabinetSub.subscription,
    isFirstTimeInCabinet: cabinetSub.isFirstTimeInCabinet
  });
  
  console.log('PricingPageState: Final decision:', {
    shouldShowPricing,
    shouldRedirectAway: !shouldShowPricing,
    isLicenseExempt,
    hasValidSubscription: cabinetSub.hasValidSubscription
  });
  
  return {
    isLoading: false,
    shouldShowPricingPage: shouldShowPricing,
    shouldRedirectAway: !shouldShowPricing, // If user doesn't need pricing, redirect away
    canAccessApp: true,
    
    // Pass through all the data PricingPage needs
    licenseUsage: cabinetSub.licenseUsage,
    isAdmin: cabinetSub.isAdmin,
    checkoutLoading: cabinetSub.checkoutLoading,
    wasInvited: cabinetSub.wasInvited,
    isNewCabinet: cabinetSub.isNewCabinet,
    
    // Pass through actions
    redirectToCabinetCheckout: cabinetSub.redirectToCabinetCheckout,
    markCabinetAsVisited: cabinetSub.markCabinetAsVisited
  };
};
